/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * Turning lookup tables into picker options.
 *
 * Which column carries a row's human label is per-table configuration, not a
 * convention: `organization` names it `name`, `activity_type` names it
 * `display_name` and has no `name` column at all. Every table declares the
 * answer as `record_identifier`, and the fetch response hands it back in
 * `table_schema` — so the label column is read from metadata rather than
 * assumed, and a picker never renders a row as a bare id.
 */

/** Columns worth trying when the declared identifier is missing from a row. */
const FALLBACK_LABEL_COLUMNS = ['display_name', 'name', 'title', 'label', 'code'];

function labelFor(record, labelKey) {
  const candidates = labelKey ? [labelKey, ...FALLBACK_LABEL_COLUMNS] : FALLBACK_LABEL_COLUMNS;
  const found = candidates
    .map((column) => record?.[column])
    .find((value) => value !== undefined && value !== null && value !== '');
  return found !== undefined ? String(found) : `#${record?.id}`;
}

/** {rows, labelKey} -> [{value, label}] for a <select>. */
export function toOptions(lookup) {
  const rows = lookup?.rows;
  if (!Array.isArray(rows)) return [];
  return rows.map((record) => ({
    value: record.id,
    label: labelFor(record, lookup.labelKey),
  }));
}

/**
 * Normalises one fetch response into {rows, labelKey}. Endpoints in this
 * codebase have returned bare arrays, {results} and {records}, so all three are
 * accepted rather than guessed at.
 */
export function readLookup(payload) {
  if (Array.isArray(payload)) return { rows: payload, labelKey: null };
  return {
    rows: payload?.records || payload?.results || payload?.data || [],
    labelKey: payload?.table_schema?.record_identifier || null,
  };
}
